import { useState, useEffect } from "react";
import { 
  Database, 
  Table2, 
  Columns3, 
  Eye,
  Search,
  ChevronRight,
  Loader2,
  FolderTree
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/services/api";

export default function TableBrowser() {
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [columns, setColumns] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState({ 
    connections: true, 
    schemas: false,
    tables: false, 
    columns: false 
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const data = await api.getOracleConnections();
      setConnections(data.filter(c => c.status === "connected"));
    } catch (error) {
      toast.error("Failed to load connections");
    } finally {
      setLoading(prev => ({ ...prev, connections: false }));
    }
  };

  const fetchSchemas = async (connectionId) => {
    setLoading(prev => ({ ...prev, schemas: true }));
    setSelectedSchema(null);
    setTables([]);
    setSelectedTable(null);
    setColumns([]);
    try {
      const data = await api.getOracleSchemas(connectionId);
      setSchemas(data.schemas || []);
    } catch (error) {
      toast.error("Failed to load schemas");
      setSchemas([]);
    } finally {
      setLoading(prev => ({ ...prev, schemas: false }));
    }
  };

  const fetchTables = async (schema, searchTerm = "") => {
    if (!selectedConnection) return;
    setLoading(prev => ({ ...prev, tables: true }));
    setSelectedTable(null);
    setColumns([]);
    try {
      const data = await api.getOracleTables(selectedConnection, schema, searchTerm || null, 100);
      setTables(data.tables || []);
    } catch (error) {
      toast.error("Failed to load tables");
      setTables([]);
    } finally {
      setLoading(prev => ({ ...prev, tables: false }));
    }
  };

  const fetchColumns = async (tableName) => {
    if (!selectedConnection) return;
    setLoading(prev => ({ ...prev, columns: true }));
    try {
      const data = await api.getOracleColumns(selectedConnection, tableName, selectedSchema);
      setColumns(data.columns || []);
    } catch (error) {
      toast.error("Failed to load columns");
      setColumns([]);
    } finally {
      setLoading(prev => ({ ...prev, columns: false }));
    }
  };

  const handleConnectionChange = (connectionId) => {
    setSelectedConnection(connectionId);
    fetchSchemas(connectionId);
  };

  const handleSchemaChange = (schema) => {
    setSelectedSchema(schema);
    setSearchQuery("");
    fetchTables(schema, "");
  };

  const handleTableSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    // Debounce the search
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      fetchTables(selectedSchema, value);
    }, 300);
    setSearchTimeout(timeout);
  };
  
  const [searchTimeout, setSearchTimeout] = useState(null);

  const handleTableSelect = (tableName) => {
    setSelectedTable(tableName);
    fetchColumns(tableName);
  };

  const handlePreview = async () => {
    if (!selectedConnection || !selectedTable) return;
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const data = await api.previewOracleData(selectedConnection, selectedTable, 10, selectedSchema);
      setPreviewData(data.data || []);
    } catch (error) {
      toast.error("Failed to load preview data");
      setPreviewData([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const filteredTables = tables; // Already filtered by backend

  const getDataTypeBadge = (dataType) => {
    const typeColors = {
      VARCHAR2: "bg-green-100 text-green-700",
      NVARCHAR2: "bg-green-100 text-green-700",
      CHAR: "bg-green-100 text-green-700",
      NCHAR: "bg-green-100 text-green-700",
      NUMBER: "bg-blue-100 text-blue-700",
      INTEGER: "bg-blue-100 text-blue-700",
      FLOAT: "bg-blue-100 text-blue-700",
      DATE: "bg-purple-100 text-purple-700",
      TIMESTAMP: "bg-purple-100 text-purple-700",
      CLOB: "bg-orange-100 text-orange-700",
      BLOB: "bg-orange-100 text-orange-700",
      LONG: "bg-yellow-100 text-yellow-700",
    };
    return typeColors[dataType] || "bg-gray-100 text-gray-700";
  };

  if (loading.connections) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loader w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="table-browser-page">
      {/* Connection & Schema Selector */}
      <Card className="shadow-soft">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            {/* Connection Selector */}
            <div className="flex items-center gap-3 flex-1">
              <Database className="w-5 h-5 text-muted-foreground" />
              <Select 
                value={selectedConnection || ""} 
                onValueChange={handleConnectionChange}
              >
                <SelectTrigger data-testid="connection-selector" className="flex-1">
                  <SelectValue placeholder="Select an Oracle connection" />
                </SelectTrigger>
                <SelectContent>
                  {connections.map((conn) => (
                    <SelectItem key={conn.id} value={conn.id}>
                      {conn.name} ({conn.host})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Schema Selector */}
            {selectedConnection && (
              <div className="flex items-center gap-3 flex-1">
                <FolderTree className="w-5 h-5 text-muted-foreground" />
                <Select 
                  value={selectedSchema || ""} 
                  onValueChange={handleSchemaChange}
                  disabled={loading.schemas}
                >
                  <SelectTrigger data-testid="schema-selector" className="flex-1">
                    <SelectValue placeholder={loading.schemas ? "Loading schemas..." : "Select a schema"} />
                  </SelectTrigger>
                  <SelectContent>
                    {schemas.map((schema) => (
                      <SelectItem key={schema.schema_name} value={schema.schema_name}>
                        {schema.schema_name} ({schema.table_count} tables)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {connections.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="empty-state">
            <Database className="empty-state-icon" />
            <h3 className="font-semibold text-lg">No Connected Oracle Databases</h3>
            <p className="text-muted-foreground text-sm mt-1 max-w-sm">
              First, add and test an Oracle connection in the Connections page
            </p>
          </CardContent>
        </Card>
      ) : !selectedConnection ? (
        <Card className="border-dashed">
          <CardContent className="empty-state">
            <Database className="empty-state-icon" />
            <h3 className="font-semibold text-lg">Select a Connection</h3>
            <p className="text-muted-foreground text-sm mt-1">
              Choose an Oracle connection above to browse schemas and tables
            </p>
          </CardContent>
        </Card>
      ) : !selectedSchema ? (
        <Card className="border-dashed">
          <CardContent className="empty-state">
            <FolderTree className="empty-state-icon" />
            <h3 className="font-semibold text-lg">Select a Schema</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {loading.schemas ? "Loading available schemas..." : `${schemas.length} schemas available`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tables List */}
          <Card className="shadow-soft">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Table2 className="w-4 h-4" />
                Tables ({filteredTables.length})
              </CardTitle>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search tables... (type to search)"
                  value={searchQuery}
                  onChange={handleTableSearch}
                  className="pl-9"
                  data-testid="table-search-input"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading.tables ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-1 p-3">
                    {filteredTables.map((table) => (
                      <button
                        key={table.table_name}
                        onClick={() => handleTableSelect(table.table_name)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors ${
                          selectedTable === table.table_name
                            ? "bg-primary text-primary-foreground"
                            : "hover:bg-muted"
                        }`}
                        data-testid={`table-item-${table.table_name}`}
                      >
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium truncate block">
                            {table.table_name}
                          </span>
                          {table.row_count > 0 && (
                            <span className={`text-xs ${selectedTable === table.table_name ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                              ~{table.row_count.toLocaleString()} rows
                            </span>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 shrink-0" />
                      </button>
                    ))}
                    {filteredTables.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No tables found
                      </p>
                    )}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Columns Panel */}
          <Card className="lg:col-span-2 shadow-soft">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Columns3 className="w-4 h-4" />
                {selectedTable ? (
                  <span>
                    <span className="text-muted-foreground">{selectedSchema}.</span>
                    {selectedTable}
                  </span>
                ) : (
                  "Select a Table"
                )}
              </CardTitle>
              {selectedTable && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handlePreview}
                  data-testid="preview-data-btn"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Preview Data
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {!selectedTable ? (
                <div className="empty-state py-12">
                  <Columns3 className="empty-state-icon" />
                  <p className="text-muted-foreground">Select a table to view its columns</p>
                </div>
              ) : loading.columns ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Column Name</TableHead>
                        <TableHead>Data Type</TableHead>
                        <TableHead>Length</TableHead>
                        <TableHead>Nullable</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {columns.map((col) => (
                        <TableRow key={col.column_name}>
                          <TableCell className="font-mono text-sm">
                            {col.column_name}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={getDataTypeBadge(col.data_type)}
                            >
                              {col.data_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {col.data_precision || col.data_length || "-"}
                          </TableCell>
                          <TableCell>
                            {col.nullable ? (
                              <Badge variant="outline">NULL</Badge>
                            ) : (
                              <Badge variant="secondary">NOT NULL</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {columns.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                            No columns found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preview Data Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Preview: {selectedSchema}.{selectedTable}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {previewLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : previewData.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No data available</p>
            ) : (
              <div className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {Object.keys(previewData[0] || {}).map((key) => (
                        <TableHead key={key} className="font-mono text-xs whitespace-nowrap">
                          {key}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.map((row, idx) => (
                      <TableRow key={idx}>
                        {Object.values(row).map((val, i) => (
                          <TableCell key={i} className="text-sm whitespace-nowrap max-w-xs truncate">
                            {val !== null ? String(val) : <span className="text-muted-foreground">NULL</span>}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
